import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Loader2, Search, Inbox, ArrowLeft, ChevronRight, ShieldCheck, Users, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, formatDate, STATUS_META } from "@/lib/qa";

export default function AdminRequests() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [status, setStatus] = useState("all");
    const [category, setCategory] = useState("all");
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => { API.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
    useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 300); return () => clearTimeout(t); }, [search]);

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (status !== "all") params.status = status;
        if (category !== "all") params.category = category;
        if (debounced) params.search = debounced;
        API.get("/admin/questions", { params })
            .then(({ data }) => setItems(data))
            .catch(e => setError(apiError(e)))
            .finally(() => setLoading(false));
    }, [status, category, debounced]);

    // Group filtered items by user (keyed by email — unique per account).
    const groups = useMemo(() => {
        const map = new Map();
        for (const q of items) {
            const key = (q.user_email || "").toLowerCase() || `__no_email_${q.user_id || q.id}`;
            let g = map.get(key);
            if (!g) {
                g = {
                    key,
                    user_name: q.user_name || "Utente senza nome",
                    user_email: q.user_email || "",
                    questions: [],
                };
                map.set(key, g);
            }
            g.questions.push(q);
        }
        const arr = Array.from(map.values());
        // sort each user's questions by created_at desc
        arr.forEach(g => g.questions.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
        // sort users alphabetically by full_name (case-insensitive)
        arr.sort((a, b) => (a.user_name || "").localeCompare(b.user_name || "", "it", { sensitivity: "base" }));
        return arr;
    }, [items]);

    const initials = (name) => (name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .map(s => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin"><ArrowLeft className="size-4 mr-1" /> Panoramica admin</Link>
            </Button>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" /> Pannello amministratore
            </div>
            <h1 className="mt-1 font-serif text-3xl font-semibold" data-testid="admin-req-title">Gestione richieste</h1>
            <p className="mt-2 text-muted-foreground text-sm">Richieste raggruppate per utente. Espandi una cartella per vedere le domande, con filtri per stato, categoria e ricerca testuale.</p>

            <Card className="mt-6 border-slate-200">
                <CardContent className="pt-5">
                    <div className="grid gap-3 md:grid-cols-3">
                        <div className="relative">
                            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca titolo, testo o utente…" className="pl-9" data-testid="admin-req-search" />
                        </div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="admin-req-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="admin-req-category"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutte le categorie</SelectItem>
                                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

            <div className="mt-4">
                {loading ? (
                    <div className="text-center py-16"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                ) : groups.length === 0 ? (
                    <Card className="border-dashed border-2 bg-muted/30"><CardContent className="py-14 text-center"><Inbox className="size-10 mx-auto text-muted-foreground/40" /><p className="mt-3 text-muted-foreground">Nessuna richiesta per i filtri selezionati.</p></CardContent></Card>
                ) : (
                    <>
                        <div className="mb-3 inline-flex items-center gap-2 text-xs text-muted-foreground" data-testid="admin-req-groups-summary">
                            <Users className="size-3.5" />
                            <span><strong className="text-slate-900">{groups.length}</strong> {groups.length === 1 ? "utente" : "utenti"} · <strong className="text-slate-900">{items.length}</strong> {items.length === 1 ? "richiesta" : "richieste"}</span>
                        </div>
                        <Accordion type="multiple" className="space-y-3" data-testid="admin-req-groups">
                            {groups.map(g => (
                                <AccordionItem
                                    key={g.key}
                                    value={g.key}
                                    className="border rounded-xl border-slate-200 bg-white overflow-hidden data-[state=open]:border-primary/30 data-[state=open]:shadow-sm"
                                    data-testid={`admin-req-group-${g.key}`}
                                >
                                    <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-slate-50/70 data-[state=open]:bg-primary/[0.04]">
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 font-semibold text-sm">
                                                {initials(g.user_name)}
                                            </div>
                                            <div className="min-w-0 flex-1 text-left">
                                                <div className="font-semibold text-slate-900 truncate" data-testid={`admin-req-group-name-${g.key}`}>{g.user_name}</div>
                                                <div className="text-xs text-muted-foreground truncate">{g.user_email || "—"}</div>
                                            </div>
                                            <span
                                                className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/20 px-2.5 py-1 text-xs font-semibold text-primary shrink-0"
                                                data-testid={`admin-req-group-count-${g.key}`}
                                            >
                                                <FolderOpen className="size-3.5" /> {g.questions.length}
                                            </span>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-5 pb-5 pt-0 border-t border-slate-100">
                                        <div className="mt-3 space-y-2" data-testid={`admin-req-group-items-${g.key}`}>
                                            {g.questions.map(q => (
                                                <Link
                                                    key={q.id}
                                                    to={`/admin/richieste/${q.id}`}
                                                    data-testid={`admin-req-item-${q.id}`}
                                                    className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-primary/30 hover:shadow-sm transition"
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                                <StatusBadge status={q.status} />
                                                                <span className="text-xs text-muted-foreground">{formatDate(q.created_at)}</span>
                                                            </div>
                                                            <div className="font-semibold text-slate-900 truncate">{q.title}</div>
                                                        </div>
                                                        <ChevronRight className="size-5 text-muted-foreground shrink-0" />
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </>
                )}
            </div>
        </div>
    );
}
